import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { Label } from "@/ui/components/ui/label";
import { useToast } from "@/controllers/hooks/use-toast";
import { Loader2, ArrowRight } from "lucide-react";
import { supabase } from "@/infra/supabase/client";
import { irParaOsAgentes } from "@/config/agentes";
import { useNavigate } from "react-router-dom";
import { logger } from "@/utils/logger";

export default function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase(),
        password: password,
      });

      if (error) {
        logger.warn("auth", "Falha no login", {
          email: email.toLowerCase(),
          error: error.message,
        });
        toast({
          title: "Erro no Login",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      if (authData.user) {
        // Verificar perfil do usuário
        const { data: profile } = await supabase
          .from("profiles")
          .select("is_admin, full_name, onboarding_completed")
          .eq("id", authData.user.id)
          .maybeSingle();

        const isAdmin = profile?.is_admin || false;
        const onboardingCompleted = profile?.onboarding_completed || false;

        logger.info("auth", "Login bem-sucedido", {
          userId: authData.user.id,
          isAdmin,
          onboardingCompleted,
        });

        toast({
          title: "Login realizado com sucesso!",
          description: "Bem-vindo de volta ao campo de batalha.",
          className: "bg-green-500 text-white border-none",
        });

        // Redirecionar baseado no tipo de usuário e status do onboarding
        setTimeout(() => {
          // O app é só do admin; o aluno usa os agentes no Claude ou no ChatGPT.
          if (isAdmin) {
            navigate("/admin");
          } else {
            void irParaOsAgentes();
          }
        }, 1000);
      }
    } catch (error) {
      logger.error("auth", "Erro inesperado no login", {
        error: error instanceof Error ? error.message : String(error),
      });
      toast({
        title: "Erro Inesperado",
        description:
          "Ocorreu um erro no sistema. Tente novamente em alguns minutos.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleLogin} className="space-y-4">
        <div className="space-y-2">
          <Label
            htmlFor="email"
            className="text-neutral-500 font-semibold text-xs uppercase tracking-[0.06em] pl-1"
          >
            Email
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="seu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="h-12 bg-white/60 border-black/[0.08] text-neutral-900 placeholder:text-neutral-400 rounded-xl transition-all hover:border-black/[0.12] focus:border-accent-800/50 focus:ring-2 focus:ring-accent-800/10"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between pl-1">
            <Label
              htmlFor="password"
              className="text-neutral-500 font-semibold text-xs uppercase tracking-[0.06em]"
            >
              Senha
            </Label>
            <Link
              to="/recuperar"
              className="text-xs text-accent-800 hover:text-accent-700 font-medium transition-colors"
            >
              Esqueceu a senha?
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="h-12 bg-white/60 border-black/[0.08] text-neutral-900 placeholder:text-neutral-400 rounded-xl transition-all hover:border-black/[0.12] focus:border-accent-800/50 focus:ring-2 focus:ring-accent-800/10"
          />
        </div>

        <div className="pt-2">
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full h-12 bg-gradient-to-r from-accent-800 to-accent-600 hover:opacity-90 text-white font-semibold rounded-xl text-base shadow-[0_4px_16px_rgba(195,150,74,0.3)] hover:shadow-[0_6px_20px_rgba(195,150,74,0.4)] hover:-translate-y-0.5 transition-all duration-300"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <ArrowRight className="w-5 h-5 mr-2" />
            )}
            {isLoading ? "Validando Acesso..." : "Acessar Sistema"}
          </Button>
        </div>
      </form>

      <div className="text-center pt-4">
        <p className="text-sm text-neutral-500">
          Não tem uma conta?{" "}
          <a
            href="/cadastro"
            className="text-accent-800 hover:text-accent-700 font-medium transition-colors"
          >
            Criar conta
          </a>
        </p>
      </div>
    </div>
  );
}
